import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../../utils/axios';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import DashboardStats from './components/DashboardStats';
import AssetDataGrid from './components/AssetDataGrid';
import FilterSidebar from './components/FilterSidebar';
import AssetDetailDrawer from './components/AssetDetailDrawer';
// import FloorPlanView from './components/FloorPlanView';
import LifecyclePipelineView from './components/LifecyclePipelineView';
import AssetRecoveryView from './components/AssetRecoveryView';
import SoftwareLicencesView from './components/SoftwareLicencesView';
import SoftwareLicenceMultiSelect from './components/SoftwareLicenceMultiSelect';

const AssetManagementDashboard = () => {

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryTab = searchParams.get('tab');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState(queryTab || 'inventory');

  useEffect(() => {
    if (queryTab && ['inventory', 'lifecycle', 'software', 'recovery',].includes(queryTab)) {
      setActiveTab(queryTab);
    }
  }, [queryTab]);

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showDetailDrawer, setShowDetailDrawer] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    asset_category_id: '',
    department: '',
    owner_id: ''  // Changed from 'owner' to 'owner_id' for consistency
  });

  const [newAsset, setNewAsset] = useState({
    name: '',
    asset_id: '',
    asset_category_id: '',
    owner_id: '',
    department: '',
    purchase_date: '',
    warranty_expiration: '',
    status: 'active',
    location: '',
    serial_number: '',
    brand: '',
    model: '',
    price: '',
    description: '',
    software_licence_id: '',
    software_licence_ids: [],
    other_software: '',
    send_email: true,
    notify_owner: true,
  });

  const [licences, setLicences] = useState([]);
  const [licencesError, setLicencesError] = useState('');
  const [softwareCategories, setSoftwareCategories] = useState([]);
  const [softwareDepartments, setSoftwareDepartments] = useState([]);
  const [softwareVendors, setSoftwareVendors] = useState([]);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [assetModalType, setAssetModalType] = useState('hardware'); // 'hardware' | 'software'

  const initialSoftwareForm = {
    software: '',
    vendor: '',
    vendorId: '',
    category: '',
    categoryId: '',
    version: '1.0',
    licenceType: 'Subscription',
    licenceKey: '',
    total: '',
    used: '0',
    renewal: '',
    cost: '',
    department: '',
    departmentId: '',
    owner: '',
    status: 'Active',
    notes: '',
  };
  const [newSoftware, setNewSoftware] = useState(initialSoftwareForm);

  const fetchAssets = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/assets');
      
      if (response.data.success) {
        setAssets(response.data.data || []);
        setError(null);
      } else {
        setError('Failed to fetch assets');
        setAssets([]);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      setError(error.response?.data?.message || 'Failed to fetch assets');
      setAssets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('/asset-categories');
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/admin/users');
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchSoftwareLicences = async () => {
    try {
      const response = await axios.get('/software-licences');
      const body = response.data;
      const rows = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
      setLicences(rows.map((row) => ({
        id: row.id, software: row.software || '', vendor: row.vendor_relation?.name || row.vendor || '',
        category: row.software_category?.name || row.category || '', version: row.version || '',
        description: row.description || '', licenceType: row.licence_type || 'Subscription',
        licenceKey: row.licence_key || '', total: Number(row.total || 0),
        used: Number(row.used || 0), renewal: row.renewal_date || null,
        purchaseDate: row.purchase_date || null, expiryDate: row.expiry_date || null,
        cost: Number(row.cost || 0), billingCycle: row.billing_cycle || 'Annual',
        currency: row.currency || 'INR', purchaseOrder: row.purchase_order || '',
        contractNumber: row.contract_number || '', owner: row.owner || '',
        department: row.department_relation?.name || row.department || '', supportContact: row.support_contact || '',
        status: row.status || 'Active', notes: row.notes || '',
      })));
      setLicencesError('');
    } catch (error) {
      console.error('Error fetching software licences:', error);
      setLicencesError(error.response?.data?.message || 'Unable to load software licences.');
      setLicences([]);
    }
  };

  const fetchSoftwareLicenceLookups = async () => {
    try {
      const [categoryResponse, departmentResponse, vendorResponse] = await Promise.all([
        axios.get('/software-categories'),
        axios.get('/departments'),
        axios.get('/vendors', { params: { per_page: 200, status: 'Active' } }),
      ]);
      const categoryBody = categoryResponse.data;
      const departmentBody = departmentResponse.data;
      const vendorBody = vendorResponse.data;
      const categoryRows = Array.isArray(categoryBody) ? categoryBody : (Array.isArray(categoryBody?.data) ? categoryBody.data : []);
      const departmentRows = Array.isArray(departmentBody) ? departmentBody : (Array.isArray(departmentBody?.data) ? departmentBody.data : []);
      const vendorPayload = vendorBody?.data;
      const vendorRows = Array.isArray(vendorPayload) ? vendorPayload : (Array.isArray(vendorPayload?.data) ? vendorPayload.data : (Array.isArray(vendorBody) ? vendorBody : []));
      setSoftwareCategories(categoryRows.filter((item) => item.is_active !== false && item.is_active !== 0));
      setSoftwareDepartments(departmentRows.filter((item) => !item.status || String(item.status).toLowerCase() === 'active'));
      setSoftwareVendors(vendorRows.filter((item) => !item.status || String(item.status).toLowerCase() === 'active'));
    } catch (error) {
      console.error('Unable to load software licence dropdown options:', error);
    }
  };

  const detectCurrentLocation = async (target = 'new') => {
    setIsDetectingLocation(true);

    const applyLocation = (locStr) => {
      if (!locStr) return;
      if (target === 'new') {
        setNewAsset((prev) => ({ ...prev, location: locStr }));
      } else if (target === 'edit') {
        setEditAsset((prev) => ({ ...prev, location: locStr }));
      }
    };

    const formatAddress = (data) => {
      if (!data) return '';
      // Support BigDataCloud, Nominatim and standard reverse-geocode structures
      const city = data.city || data.locality || data.address?.city || data.address?.town || data.address?.village || data.address?.suburb || data.address?.district || '';
      const state = data.principalSubdivision || data.address?.state || data.address?.region || '';
      const country = data.countryName || data.address?.country || '';
      const parts = [];
      if (city) parts.push(city);
      if (state && state.toLowerCase() !== city.toLowerCase()) parts.push(state);
      if (country) parts.push(country);
      return parts.length > 0 ? parts.join(', ') : (data.localityInfo?.informative?.[0]?.name || data.display_name || '');
    };

    const fallbackToIp = async () => {
      try {
        const ipRes = await fetch('https://ipapi.co/json/');
        if (ipRes.ok) {
          const ipData = await ipRes.json();
          const parts = [];
          if (ipData.city) parts.push(ipData.city);
          if (ipData.region && ipData.region !== ipData.city) parts.push(ipData.region);
          if (ipData.country_name) parts.push(ipData.country_name);
          if (parts.length > 0) {
            applyLocation(parts.join(', '));
            return true;
          }
        }
      } catch (e) {
        console.warn('ipapi.co failed, attempting freeipapi:', e);
      }

      try {
        const ipRes2 = await fetch('https://freeipapi.com/api/json');
        if (ipRes2.ok) {
          const ipData2 = await ipRes2.json();
          const parts2 = [];
          if (ipData2.cityName) parts2.push(ipData2.cityName);
          if (ipData2.regionName && ipData2.regionName !== ipData2.cityName) parts2.push(ipData2.regionName);
          if (ipData2.countryName) parts2.push(ipData2.countryName);
          if (parts2.length > 0) {
            applyLocation(parts2.join(', '));
            return true;
          }
        }
      } catch (e2) {
        console.warn('freeipapi failed:', e2);
      }

      return false;
    };

    if (!navigator.geolocation) {
      const ok = await fallbackToIp();
      if (!ok) alert('Geolocation is not supported by your browser. Please type location manually.');
      setIsDetectingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          let locName = '';

          // 1. First attempt: BigDataCloud (fast, client-side, CORS enabled, no API key required)
          try {
            const bdcRes = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            );
            if (bdcRes.ok) {
              const bdcData = await bdcRes.json();
              locName = formatAddress(bdcData);
            }
          } catch (e) {
            console.warn('BigDataCloud reverse geocode failed, falling back to Nominatim:', e);
          }

          // 2. Second attempt: OpenStreetMap Nominatim
          if (!locName) {
            try {
              const osmRes = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
              );
              if (osmRes.ok) {
                const osmData = await osmRes.json();
                locName = formatAddress(osmData);
              }
            } catch (e2) {
              console.warn('Nominatim reverse geocode failed:', e2);
            }
          }

          if (locName) {
            applyLocation(locName);
          } else {
            const ok = await fallbackToIp();
            if (!ok) {
              applyLocation(`Coordinates: ${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
            }
          }
        } catch (err) {
          console.error('Error during reverse geocoding:', err);
          await fallbackToIp();
        } finally {
          setIsDetectingLocation(false);
        }
      },
      async (geoError) => {
        console.warn('Geolocation lookup or permission error:', geoError.message);
        const ok = await fallbackToIp();
        if (!ok) {
          alert('Could not auto-detect location. Please check browser permissions or type location manually.');
        }
        setIsDetectingLocation(false);
      },
      { timeout: 8000, enableHighAccuracy: true, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    const loadData = async () => {
      setDataLoaded(false);
      await Promise.all([
        fetchAssets(),
        fetchCategories(),
        fetchUsers(),
        fetchSoftwareLicences(),
        fetchSoftwareLicenceLookups()
      ]);
      setDataLoaded(true);
    };
    
    loadData();
  }, []);

  const handleCreateAsset = async () => {
    if (!newAsset.asset_category_id || !newAsset.owner_id || !newAsset.department) {
      alert('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...newAsset,
        send_email: newAsset.send_email !== false,
        notify_owner: newAsset.send_email !== false,
        software_licence_ids: newAsset.software_licence_ids || [],
        software_licence_id: (newAsset.software_licence_ids && newAsset.software_licence_ids.length > 0)
          ? newAsset.software_licence_ids[0]
          : (newAsset.software_licence_id || null),
      };
      const response = await axios.post('/assets', payload);
      
      if (response.data.success) {
        await fetchAssets();
        setShowAddModal(false);
        setNewAsset({
          name: '',
          asset_id: '',
          asset_category_id: '',
          owner_id: '',
          department: '',
          purchase_date: '',
          warranty_expiration: '',
          status: 'active',
          location: '',
          serial_number: '',
          brand: '',
          model: '',
          price: '',
          description: '',
          software_licence_id: '',
          software_licence_ids: [],
          other_software: '',
          send_email: true,
          notify_owner: true,
        });
        const emailMsg = response.data.email_sent ? '\n\n✓ An official assignment email was dispatched to the employee.' : '';
        alert((response.data.message || 'Asset created successfully') + emailMsg);
      } else {
        alert(response.data.message || 'Failed to create asset');
      }
    } catch (error) {
      console.error('Error creating asset:', error);
      alert(error.response?.data?.message || 'Failed to create asset');
    } finally {
      setLoading(false);
    }
  };

  const filteredAssets = assets?.filter(asset => {
    return (
      (!filters?.status || asset?.status === filters?.status) &&
      (!filters?.asset_category_id || asset?.asset_category_id == filters?.asset_category_id) &&
      (!filters?.department || asset?.department === filters?.department) && 
      (!filters?.owner_id || asset?.owner_id == filters?.owner_id)
    );
  }) || [];

  const handleAssetClick = (asset) => {

    
    if (!asset?.id) {
      alert('Asset ID not found');
      return;
    }
    setSelectedAsset(asset);
    setShowDetailDrawer(true)
  };

  const handleEditAsset = (asset) => {
    const currentLicenceIds = Array.isArray(asset.software_licences) && asset.software_licences.length > 0
      ? asset.software_licences.map(l => String(l.id))
      : (asset.software_licence_id ? [String(asset.software_licence_id)] : []);

    setEditAsset({
      ...asset,
      software_licence_ids: currentLicenceIds,
      software_licence_id: currentLicenceIds[0] || '',
    });
    setShowEditModal(true);
  };

  const handleAssetUpdate = async (updatedAsset) => {
    try {
      setLoading(true);
      const payload = {
        ...updatedAsset,
        send_email: updatedAsset.send_email !== false,
        notify_owner: updatedAsset.send_email !== false,
        software_licence_ids: updatedAsset.software_licence_ids || [],
        software_licence_id: (updatedAsset.software_licence_ids && updatedAsset.software_licence_ids.length > 0)
          ? updatedAsset.software_licence_ids[0]
          : (updatedAsset.software_licence_id || null),
      };
      const response = await axios.put(`/assets/${updatedAsset.id}`, payload);
      
      if (response.data.success) {
        await fetchAssets();
        setSelectedAsset(response.data.data);
        const emailMsg = response.data.email_sent ? ' (Assignment email dispatched to employee)' : '';
        alert((response.data.message || 'Asset updated successfully') + emailMsg);
        return response.data.data;
      }
    } catch (error) {
      console.error('Error updating asset:', error);
      alert('Failed to update asset');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAsset = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.delete(`/assets/${id}`);
      
      if (response.data.success) {
        await fetchAssets();
        setShowDetailDrawer(false);
        setSelectedAsset(null);
        alert('Asset deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      alert('Failed to delete asset');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationUpdate = (assetId, newLocation) => {
    setAssets(prev => prev?.map(asset => 
      asset?.id === assetId ? { ...asset, location: newLocation } : asset
    ));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: '',
      asset_category_id: '',
      department: '',
      owner_id: ''
    });
  };

  const handleBulkAction = async (action) => {
    try {
      if (action === 'status') {
        const status = prompt('Enter status: active/spare/repair/retired');
        if (!status) return;
        
        setLoading(true);
        const response = await axios.post('/assets/bulk-status', {
          ids: selectedAssets,
          status
        });
        
        if (response.data.success) {
          await fetchAssets();
          setSelectedAssets([]);
          alert('Bulk status update completed');
        }
      }
      
      if (action === 'export') {
        const csvRows = [];
        csvRows.push(['Asset ID', 'Type', 'Owner', 'Department', 'Status', 'Location']);
        
        filteredAssets.forEach(asset => {
          csvRows.push([
            asset.asset_id || '',
            asset?.category?.name || '',
            asset?.owner?.name || '',
            asset.department || '',
            asset.status || '',
            asset.location || ''
          ]);
        });
        
        const csvContent = csvRows.map(e => e.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `assets_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        alert('Export completed successfully');
      }
    } catch (error) {
      console.error('Error in bulk action:', error);
      alert('Failed to complete bulk action');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreset = () => {
    console.log('Saving filter preset:', filters);
    alert('Filter preset saved successfully');
  };

  const handleSyncERP = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/assets/sync-erp');
      
      if (response.data.success) {
        await fetchAssets();
        alert('ERP synchronization completed successfully');
      }
    } catch (error) {
      console.error('Error syncing ERP:', error);
      alert('Failed to sync with ERP');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewAsset(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSoftwareInputChange = (field, value) => {
    setNewSoftware(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateSoftwareLicence = async (e) => {
    if (e) e.preventDefault();
    if (!newSoftware.software.trim()) return alert('Software name is required');
    if (!newSoftware.vendorId) return alert('Vendor is required');
    if (!newSoftware.categoryId) return alert('Category is required');
    if (!newSoftware.total || Number(newSoftware.total) <= 0) return alert('Total licences must be greater than 0');
    if (Number(newSoftware.used || 0) > Number(newSoftware.total)) return alert('Used licences cannot exceed total licences');

    try {
      setLoading(true);
      await axios.post('/software-licences', {
        software: newSoftware.software.trim(),
        vendor_id: Number(newSoftware.vendorId),
        category_id: Number(newSoftware.categoryId),
        department_id: newSoftware.departmentId ? Number(newSoftware.departmentId) : null,
        version: newSoftware.version || '1.0',
        licence_type: newSoftware.licenceType,
        licence_key: newSoftware.licenceKey.trim() || null,
        total: Number(newSoftware.total),
        used: Number(newSoftware.used || 0),
        renewal_date: newSoftware.licenceType === 'Perpetual' ? null : (newSoftware.renewal || null),
        expiry_date: newSoftware.licenceType === 'Perpetual' ? null : (newSoftware.renewal || null),
        cost: Number(newSoftware.cost || 0),
        department: softwareDepartments.find((item) => String(item.id) === String(newSoftware.departmentId))?.name || null,
        owner: newSoftware.owner || null,
        status: newSoftware.status || 'Active',
        notes: newSoftware.notes || null,
      });
      await fetchSoftwareLicences();
      setShowAddModal(false);
      setNewSoftware(initialSoftwareForm);
      alert('Software licence saved successfully.');
    } catch (error) {
      const errors = error.response?.data?.errors;
      alert(errors ? Object.values(errors).flat().join(' ') : (error.response?.data?.message || 'Unable to save software licence.'));
    } finally {
      setLoading(false);
    }
  };

  // Show loading only while initial data is being fetched
  if (!dataLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Asset Management Dashboard - AssetTracker Pro</title>
        <meta name="description" content="Comprehensive IT asset management dashboard for tracking, monitoring, and managing organizational assets with real-time visibility and audit capabilities." />
      </Helmet>

      <Header />
      
      <div className="flex">
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        
        <main className={`flex-1 transition-layout ${
          sidebarCollapsed ? 'ml-16' : 'ml-72'
        } mt-16`}>
          <div className="p-8">
            <div className="max-w-full">
              {/* Page Header */}
              <div className="mb-8">
                <Breadcrumb />
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h1 className="text-3xl font-bold text-foreground">Asset Management Dashboard</h1>
                    <p className="text-muted-foreground mt-2">
                      Comprehensive visibility and control over your IT asset lifecycle
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="outline"
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => handleBulkAction('export')}
                      disabled={loading}
                    >
                      Export Data
                    </Button>
                    <Button
                      variant="outline"
                      iconName="RefreshCw"
                      iconPosition="left"
                      onClick={handleSyncERP}
                      disabled={loading}
                    >
                      Sync ERP
                    </Button>
                    <Button
                      variant="default"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => {
                        setAssetModalType(activeTab === 'software' ? 'software' : 'hardware');
                        setShowAddModal(true);
                      }}
                      disabled={loading}
                    >
                      Add Asset
                    </Button>
                  </div>
                </div>
              </div>

              {/* Dashboard Stats */}
              <DashboardStats assets={filteredAssets} />

              {/* Tab Navigation */}
              <div className="mb-6">
                <div className="border-b border-border">
                  <nav className="flex space-x-2 md:space-x-6 overflow-x-auto">
                    <button
                      onClick={() => handleTabChange('inventory')}
                      className={`py-3 px-2 border-b-2 font-medium text-sm transition-smooth whitespace-nowrap ${
                        activeTab === 'inventory' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon name="Table" size={16} />
                        <span>Inventory Grid</span>
                      </div>
                    </button>
                    <button
                      onClick={() => handleTabChange('lifecycle')}
                      className={`py-3 px-2 border-b-2 font-medium text-sm transition-smooth whitespace-nowrap ${
                        activeTab === 'lifecycle' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon name="Workflow" size={16} />
                        <span>Lifecycle Pipeline</span>
                      </div>
                    </button>
                    <button
                      onClick={() => handleTabChange('software')}
                      className={`py-3 px-2 border-b-2 font-medium text-sm transition-smooth whitespace-nowrap ${
                        activeTab === 'software' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon name="Laptop" size={16} />
                        <span>Software & Licences</span>
                      </div>
                    </button>
                    <button
                      onClick={() => handleTabChange('recovery')}
                      className={`py-3 px-2 border-b-2 font-medium text-sm transition-smooth whitespace-nowrap ${
                        activeTab === 'recovery' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <Icon name="ShieldCheck" size={16} />
                        <span>Asset Recovery & Clearance</span>
                      </div>
                    </button>
                    
                  </nav>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex gap-6 flex-wrap lg:flex-nowrap">
                {/* Filter Sidebar */}
                {activeTab === 'inventory' && showFilters && (
                  <FilterSidebar
                    filters={filters}
                    users={users}
                    categories={categories}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    onSavePreset={handleSavePreset}
                  />
                )}

                {/* Content Area */}
                <div className={`flex-1 ${activeTab === 'inventory' && showFilters ? '' : 'max-w-full'}`}>
                  {/* Filter Toggle for Inventory */}
                  {activeTab === 'inventory' && (
                    <div className="mb-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowFilters(!showFilters)}
                        iconName={showFilters ? "PanelLeftClose" : "PanelLeftOpen"}
                        iconPosition="left"
                        disabled={loading}
                      >
                        {showFilters ? 'Hide' : 'Show'} Filters
                      </Button>
                    </div>
                  )}

                  {/* Error Display */}
                  {error && (
                    <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
                      {error}
                    </div>
                  )}

                  {/* Loading Indicator */}
                  {loading && (
                    <div className="flex justify-center items-center p-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  )}

                  {/* Tab Content */}
                  {!loading && (
                    <>
                      {activeTab === 'inventory' && (
                        <AssetDataGrid
                          assets={filteredAssets}
                          selectedAssets={selectedAssets}
                          onSelectionChange={setSelectedAssets}
                          onAssetClick={handleAssetClick}
                          onBulkAction={handleBulkAction}
                        />
                      )}
                      {activeTab === 'lifecycle' && (
                        <LifecyclePipelineView
                          rawAssets={assets}
                          users={users}
                          categories={categories}
                          softwareLicences={licences}
                          onAssetClick={handleAssetClick}
                          onOpenAddAsset={() => setShowAddModal(true)}
                        />
                      )}
                      {activeTab === 'software' && (
                        <>
                        {licencesError && <div role="alert" className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{licencesError} <button type="button" className="underline ml-2" onClick={fetchSoftwareLicences}>Retry</button></div>}
                        <SoftwareLicencesView
                          licences={licences}
                          setLicences={setLicences}
                        />
                        </>
                      )}
                      {activeTab === 'recovery' && (
                        <AssetRecoveryView />
                      )}
                      {activeTab === 'floorplan' && (
                        <FloorPlanView
                          assets={filteredAssets}
                          onAssetClick={handleAssetClick}
                          onLocationUpdate={handleLocationUpdate}
                        />
                      )}
                    </>
                  )}
                </div>

                {/* Asset Detail Drawer */}
                <AssetDetailDrawer
                  asset={selectedAsset}
                  users={users}
                  categories={categories}
                  onEdit={handleEditAsset}
                  isOpen={showDetailDrawer}
                  onClose={() => setShowDetailDrawer(false)}
                  onUpdate={handleAssetUpdate}
                  onDelete={handleDeleteAsset}
                />
              </div>

              {/* Footer with Audit Instructions */}
              <div className="mt-12 p-6 bg-muted/30 rounded-lg border border-border">
                <div className="flex items-start space-x-4">
                  <Icon name="Info" size={20} className="text-primary mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">
                      Quarterly Audit Instructions
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">
                      The next quarterly audit is scheduled for September 2025. Ensure all asset locations are verified and status updates are current.
                    </p>
                    <div className="flex items-center space-x-4">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ClipboardCheck"
                        iconPosition="left"
                        disabled={loading}
                      >
                        Start Audit Process
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="FileText"
                        iconPosition="left"
                        disabled={loading}
                      >
                        View Audit Guidelines
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Add Asset Modal (Unified Hardware & Software) */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 w-full max-w-3xl p-6 overflow-y-auto max-h-[90vh] shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200 dark:border-gray-700 mb-5">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add New Asset</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Select whether you are registering physical hardware or digital software licences.
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowAddModal(false)}
              >
                <Icon name="X" size={18} />
              </Button>
            </div>

            {/* Segmented Switcher Pill */}
            <div className="bg-gray-100 dark:bg-gray-700/60 p-1 rounded-xl flex items-center mb-6 max-w-md mx-auto">
              <button
                type="button"
                onClick={() => setAssetModalType('hardware')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center space-x-2 transition-all ${
                  assetModalType === 'hardware'
                    ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                <Icon name="Laptop" size={15} />
                <span>Hardware Asset</span>
              </button>
              <button
                type="button"
                onClick={() => setAssetModalType('software')}
                className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold flex items-center justify-center space-x-2 transition-all ${
                  assetModalType === 'software'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                }`}
              >
                <Icon name="KeyRound" size={15} />
                <span>Software & Licence</span>
              </button>
            </div>

            {/* Hardware Form */}
            {assetModalType === 'hardware' && (
              <div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Asset Type *
                      </label>
                      <select
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newAsset.asset_category_id}
                        onChange={(e) => handleInputChange('asset_category_id', e.target.value)}
                      >
                        <option value="">Select Asset Type</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Assign Owner *</label>
                      <select
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newAsset.owner_id}
                        onChange={(e) => {
                          const selectedOwnerId = e.target.value;
                          const selectedUser = users.find((user) => String(user.id) === String(selectedOwnerId));
                          setNewAsset((prev) => ({
                            ...prev,
                            owner_id: selectedOwnerId,
                            department: selectedUser?.department || prev.department || '',
                          }));
                        }}
                      >
                        <option value="">Select Assign Owner</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name} {user.department ? `(${user.department})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Department *</label>
                      <input
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="Auto-filled from assigned owner"
                        value={newAsset.department}
                        onChange={(e) => handleInputChange('department', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Purchase Date</label>
                      <input
                        type="date"
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newAsset.purchase_date}
                        onChange={(e) => handleInputChange('purchase_date', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Warranty Expiration</label>
                      <input
                        type="date"
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newAsset.warranty_expiration}
                        onChange={(e) => handleInputChange('warranty_expiration', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1 flex items-center justify-between">
                        <span>Location</span>
                        <span className="text-[11px] text-muted-foreground font-normal">Click icon to auto-detect or type</span>
                      </label>
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          className="w-full border rounded-lg p-2 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700 text-sm"
                          placeholder="e.g., Patna, Bihar (or click icon to fetch)"
                          value={newAsset.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => detectCurrentLocation('new')}
                          disabled={isDetectingLocation}
                          title="Click to auto-fetch your current remote location"
                          className="absolute right-2 p-1.5 rounded-md hover:bg-muted text-primary transition-all disabled:opacity-50"
                        >
                          <Icon
                            name={isDetectingLocation ? 'Loader2' : 'MapPin'}
                            size={18}
                            className={isDetectingLocation ? 'animate-spin text-primary' : 'text-primary hover:scale-110 transition-transform'}
                          />
                        </button>
                      </div>
                      {isDetectingLocation && (
                        <p className="text-[11px] text-primary mt-1 animate-pulse flex items-center gap-1">
                          <Icon name="LocateFixed" size={12} />
                          Auto-fetching your current remote location...
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Serial Number</label>
                      <input
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="Enter serial number"
                        value={newAsset.serial_number}
                        onChange={(e) => handleInputChange('serial_number', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Asset Name</label>
                      <input
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="e.g., MacBook Air M2 (optional, auto-generated if blank)"
                        value={newAsset.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Brand</label>
                      <input
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="e.g., Dell, HP, Apple"
                        value={newAsset.brand}
                        onChange={(e) => handleInputChange('brand', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Model</label>
                      <input
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="Enter model number"
                        value={newAsset.model}
                        onChange={(e) => handleInputChange('model', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Price (₹)</label>
                      <input
                        type="number"
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="Enter price"
                        value={newAsset.price}
                        onChange={(e) => handleInputChange('price', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Status</label>
                      <select
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newAsset.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                      >
                        <option value="active">Active</option>
                        <option value="spare">Spare</option>
                        <option value="repair">Under Repair</option>
                        <option value="retired">Retired</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <SoftwareLicenceMultiSelect
                      licences={licences}
                      selectedIds={newAsset.software_licence_ids || (newAsset.software_licence_id ? [newAsset.software_licence_id] : [])}
                      onChange={(selectedIds) => {
                        const selectedLics = licences.filter((l) => selectedIds.includes(String(l.id)));
                        const names = selectedLics.map((l) => `${l.software}${l.version ? ` v${l.version}` : ''}`).join(', ');
                        setNewAsset((prev) => ({
                          ...prev,
                          software_licence_ids: selectedIds,
                          software_licence_id: selectedIds[0] || '',
                          other_software: names,
                        }));
                      }}
                      label="Software & Licences"
                      helperText="(Select multiple software to assign with this asset)"
                      placeholder="Select one or more software licences..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                      rows="3"
                      placeholder="Enter additional details about the hardware asset"
                      value={newAsset.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                    />
                  </div>

                  {/* Email Notification Option */}
                  <div className="p-3 bg-blue-50/60 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg space-y-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newAsset.send_email !== false}
                        onChange={(e) => setNewAsset((prev) => ({ ...prev, send_email: e.target.checked, notify_owner: e.target.checked }))}
                        className="rounded border-border text-primary focus:ring-blue-500 h-4 w-4"
                      />
                      <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                        <Icon name="Mail" size={15} className="text-blue-600 dark:text-blue-400" />
                        Send official assignment email to assigned user
                      </span>
                    </label>
                    <p className="text-xs text-muted-foreground pl-6">
                      Automatically dispatches an official deployment email to the custodian with hardware specifications, asset tag, and attached software licences.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowAddModal(false)}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleCreateAsset}
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Hardware Asset'}
                  </Button>
                </div>
              </div>
            )}

            {/* Software & Licence Form */}
            {assetModalType === 'software' && (
              <form onSubmit={handleCreateSoftwareLicence}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium mb-1">
                        Software Application Name *
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="e.g., JetBrains All Products Pack, Adobe Creative Cloud"
                        value={newSoftware.software}
                        onChange={(e) => handleSoftwareInputChange('software', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Vendor *</label>
                      <select
                        required
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newSoftware.vendorId}
                        onChange={(e) => { const selected = softwareVendors.find((item) => String(item.id) === e.target.value); handleSoftwareInputChange('vendorId', e.target.value); handleSoftwareInputChange('vendor', selected?.name || ''); }}
                      >
                        <option value="">Select vendor</option>
                        {softwareVendors.map((vendor) => <option key={vendor.id} value={String(vendor.id)}>{vendor.name}{vendor.code ? ` (${vendor.code})` : ''}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Category</label>
                      <select
                        required
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newSoftware.categoryId}
                        onChange={(e) => { const selected = softwareCategories.find((item) => String(item.id) === e.target.value); handleSoftwareInputChange('categoryId', e.target.value); handleSoftwareInputChange('category', selected?.name || ''); }}
                      >
                        <option value="">Select category</option>
                        {softwareCategories.map((category) => <option key={category.id} value={String(category.id)}>{category.name}{category.code ? ` (${category.code})` : ''}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Licence Type</label>
                      <select
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newSoftware.licenceType}
                        onChange={(e) => handleSoftwareInputChange('licenceType', e.target.value)}
                      >
                        <option value="Subscription">Subscription</option>
                        <option value="Perpetual">Perpetual</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Total Seats *</label>
                      <input
                        type="number"
                        min="1"
                        required
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="e.g., 50"
                        value={newSoftware.total}
                        onChange={(e) => handleSoftwareInputChange('total', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Seats In Use / Allocated</label>
                      <input
                        type="number"
                        min="0"
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="e.g., 10"
                        value={newSoftware.used}
                        onChange={(e) => handleSoftwareInputChange('used', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Annual Cost (₹)</label>
                      <input
                        type="number"
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="e.g., 185000"
                        value={newSoftware.cost}
                        onChange={(e) => handleSoftwareInputChange('cost', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Renewal / Expiry Date</label>
                      <input
                        type="date"
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newSoftware.renewal}
                        onChange={(e) => handleSoftwareInputChange('renewal', e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">Department</label>
                      <select
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        value={newSoftware.departmentId}
                        onChange={(e) => { const selected = softwareDepartments.find((item) => String(item.id) === e.target.value); handleSoftwareInputChange('departmentId', e.target.value); handleSoftwareInputChange('department', selected?.name || ''); }}
                      >
                        <option value="">Select department</option>
                        {softwareDepartments.map((department) => <option key={department.id} value={String(department.id)}>{department.name}{department.code ? ` (${department.code})` : ''}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">License Key / Contract No</label>
                      <input
                        type="text"
                        className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                        placeholder="e.g., JB-ENT-2026-99"
                        value={newSoftware.licenceKey}
                        onChange={(e) => handleSoftwareInputChange('licenceKey', e.target.value)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Notes / Description</label>
                    <textarea
                      className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-700"
                      rows="3"
                      placeholder="Enter subscription details, tier info, or special handling notes"
                      value={newSoftware.notes}
                      onChange={(e) => handleSoftwareInputChange('notes', e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="default"
                    type="submit"
                  >
                    Add Software Licence
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* EDIT ASSET MODAL */}
      {showEditModal && editAsset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 w-full max-w-4xl p-6 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold">Edit Asset</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowEditModal(false)}>
                <Icon name="X" size={18} />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Asset Type</label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={editAsset.asset_category_id}
                  onChange={(e) => setEditAsset({ ...editAsset, asset_category_id: e.target.value })}
                >
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Assign Owner</label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={editAsset.owner_id || ''}
                  onChange={(e) => {
                    const selectedUser = users.find(u => String(u.id) === String(e.target.value));
                    setEditAsset({
                      ...editAsset,
                      owner_id: e.target.value,
                      department: selectedUser?.department || editAsset.department || ''
                    });
                  }}
                >
                  <option value="">Select Assign Owner</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} {user.department ? `(${user.department})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Department</label>
                <input
                  className="w-full border rounded-lg p-2"
                  placeholder="Auto-filled from assigned owner"
                  value={editAsset.department || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, department: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 flex items-center justify-between">
                  <span>Location</span>
                  <span className="text-[11px] text-muted-foreground font-normal">Auto-detect or type</span>
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    className="w-full border rounded-lg p-2 pr-10 text-sm"
                    placeholder="e.g., Patna, Bihar (or click icon to fetch)"
                    value={editAsset.location || ''}
                    onChange={(e) => setEditAsset({ ...editAsset, location: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => detectCurrentLocation('edit')}
                    disabled={isDetectingLocation}
                    title="Click to auto-fetch current remote location"
                    className="absolute right-2 p-1.5 rounded-md hover:bg-muted text-primary transition-all disabled:opacity-50"
                  >
                    <Icon
                      name={isDetectingLocation ? 'Loader2' : 'MapPin'}
                      size={18}
                      className={isDetectingLocation ? 'animate-spin text-primary' : 'text-primary hover:scale-110 transition-transform'}
                    />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Asset Name</label>
                <input
                  className="w-full border rounded-lg p-2"
                  placeholder="e.g., MacBook Pro 16 or Dell XPS"
                  value={editAsset.name || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Brand</label>
                <input
                  className="w-full border rounded-lg p-2"
                  value={editAsset.brand || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, brand: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Model</label>
                <input
                  className="w-full border rounded-lg p-2"
                  value={editAsset.model || ''}
                  onChange={(e) => setEditAsset({ ...editAsset, model: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  className="w-full border rounded-lg p-2"
                  value={editAsset.status || 'active'}
                  onChange={(e) => setEditAsset({ ...editAsset, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="spare">Spare</option>
                  <option value="repair">Under Repair</option>
                  <option value="retired">Retired</option>
                </select>
              </div>

              <div className="col-span-1 md:col-span-2">
                <SoftwareLicenceMultiSelect
                  licences={licences}
                  selectedIds={editAsset.software_licence_ids || (editAsset.software_licence_id ? [editAsset.software_licence_id] : [])}
                  onChange={(selectedIds) => {
                    const selectedLics = licences.filter((l) => selectedIds.includes(String(l.id)));
                    const names = selectedLics.map((l) => `${l.software}${l.version ? ` v${l.version}` : ''}`).join(', ');
                    setEditAsset((prev) => ({
                      ...prev,
                      software_licence_ids: selectedIds,
                      software_licence_id: selectedIds[0] || '',
                      other_software: names,
                    }));
                  }}
                  label="Software & Licences"
                  helperText="(Update assigned enterprise software & licences)"
                  placeholder="Select one or more software licences..."
                />
              </div>

              <div className="col-span-1 md:col-span-2 p-3 bg-blue-50/60 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 rounded-lg space-y-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editAsset.send_email !== false}
                    onChange={(e) => setEditAsset((prev) => ({ ...prev, send_email: e.target.checked, notify_owner: e.target.checked }))}
                    className="rounded border-border text-primary focus:ring-blue-500 h-4 w-4"
                  />
                  <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <Icon name="Mail" size={15} className="text-blue-600 dark:text-blue-400" />
                    Send email notification to assigned custodian if changed
                  </span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button onClick={async () => {
                await handleAssetUpdate(editAsset);
                setShowEditModal(false);
              }}>
                Update Asset
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetManagementDashboard;